import { type CSSProperties, type SyntheticEvent, memo } from "react";

import clsx from "clsx";

import { useViewportInfoContext } from "@/src/contexts";
import { browserStatics } from "@/src/shared/BrowserStatic";
import type { PropsWithStyleX } from "@stylex/tokens.stylex";
import * as stylex from "@stylexjs/stylex";

import * as css from "./ImageLoader.module.css";

export type IAllowedSizes = 300 | 360 | 480 | 533 | 600 | 800 | 1000 | 1200 | 1280 | 1400 | 1600;

export type IImageLoader = PropsWithStyleX & {
    src: string;
    className?: string;
    srcSetAxis?: "width" | "height";
    alt?: string;
    lazyLoading?: boolean;
    style?: CSSProperties;
    onLoaded?: (event: SyntheticEvent<HTMLImageElement>) => void;
    size?: IAllowedSizes;
    sizeLaptop?: IAllowedSizes;
    sizeTablet?: IAllowedSizes;
    sizeMobile?: IAllowedSizes;
    height?: number;
    width?: number;
    bypassFormats?: boolean;
};

const ImageLoader = memo(
    (props: IImageLoader) => {
        const { isLaptop, isMobile, isTablet } = useViewportInfoContext();
        let src = props.src + `?${props.bypassFormats ? "" : browserStatics.imageSupportFormat || ""}`;
        if (props.size) {
            const srcSetAxis = props.srcSetAxis === "height" ? "h" : "w";
            if (isMobile && props.sizeMobile) src += `&${srcSetAxis}=${props.sizeMobile}`;
            else if (isTablet && props.sizeTablet) src += `&${srcSetAxis}=${props.sizeTablet}`;
            else if (isLaptop && props.sizeLaptop) src += `&${srcSetAxis}=${props.sizeLaptop}`;
            else src += `&${srcSetAxis}=${props.size}`;
        }

        return (
            <img
                className={clsx(
                    css.imageLoaderComponent,
                    props.className,
                    stylex.props(props.styleX).className,
                )}
                style={props.style}
                src={src}
                loading={props.lazyLoading ? "lazy" : undefined}
                alt={props.alt}
                onLoad={props.onLoaded}
                height={props.height}
                width={props.width}
            />
        );
    },
    (prevProps, nextProps) =>
        prevProps.src === nextProps.src && prevProps.srcSetAxis === nextProps.srcSetAxis,
);

export default ImageLoader;
